import * as React from "react";

import { definedMapWithDefault } from "collection-utils";

import { asString, asNumber } from "common/components/component-helpers";
import type { Value } from "common/components/types";
import { LOADING, ActionSource } from "common/components/types";
import type { GlideFunctionalRenderer } from "common/components/uis";
import type { ExpandableOptionValue } from "../ui/smart/expandable-options/expandable-options";
import ExpandableOptions from "../ui/smart/expandable-options/expandable-options";
import { getLocalizedString } from "common/components/localization";
import { useForceValueProducers } from "hooks/use-value-producers";
import { useForce } from "hooks/use-force";
import { useOnClickFromAction } from "hooks/use-on-click-from-action";
import { useEditComponentEnabledViaNetworkStatus } from "hooks/use-edit-components-enabled-via-network-status";

export const LikeMaterialRenderer: GlideFunctionalRenderer = p => {
    const { env, getValueProducers, ctx } = p;
    const [force] = useForce();
    const [[valuePV, onChangePV, captionPV], [valueProducer]] = useForceValueProducers(getValueProducers, force, ctx);
    const { onClick: onChangeCB } = useOnClickFromAction(onChangePV, ActionSource.DateTimeField, ctx, env);
    const [editingAllowed] = useEditComponentEnabledViaNetworkStatus(env);
    const { appKind } = env.appEnvironment;

    const convertIn = React.useCallback((input: Value): number => {
        return asNumber(input);
    }, []);

    const convertOut = React.useCallback((output: ExpandableOptionValue): Value => {
        return output ?? output;
    }, []);

    const onChange = React.useCallback(
        async (newValue: ExpandableOptionValue) => {
            if (valueProducer === undefined) return;

            valueProducer.set(convertOut(newValue));
            if (onChangeCB !== undefined) {
                await onChangeCB(undefined);
            }
        },
        [convertOut, valueProducer, onChangeCB]
    );

    if (captionPV === LOADING || valuePV === LOADING || onChangePV === LOADING) {
        return env.renderProgress();
    }

    const value = definedMapWithDefault(valuePV, 0, asNumber);
    const caption = definedMapWithDefault(captionPV, "", asString);

    const options = [
        { value: -1, label: getLocalizedString("disliked", appKind), icon: "22-04-dislike-1-alternate" },
        { value: 1, label: getLocalizedString("liked", appKind), icon: "22-04-like-1-alternate" },
    ];

    const isEnabled = editingAllowed;

    return (
        <ExpandableOptions
            value={convertIn(value)}
            caption={caption}
            onChange={onChange}
            isSvg={true}
            isEnabled={isEnabled}
            defaultIcon="22-04-like-1-alternate"
            options={options}
        />
    );
};
